import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Trash2, MessageCircle, Users } from 'lucide-react';
import api from '../api';

const ChatList = ({ isOpen, onClose, userId, onOpenPrivateChat, onOpenGroupChat }) => {
  const [recentChats, setRecentChats] = useState([]);
  const [swipeId, setSwipeId] = useState(null);
  const [swipeX, setSwipeX] = useState(0);
  const startXRef = useRef(null);

  useEffect(() => {
    if (isOpen && userId) {
      loadRecentChats();
    }
  }, [isOpen, userId]);

  const loadRecentChats = async () => {
    try {
      const friends = await api.getFriends(userId);
      const groups = await api.getAvailableGroups(userId);
      
      const privateChats = await Promise.all(
        friends.slice(0, 10).map(async (friend) => {
          const chatId = [userId, friend.friendId].sort().join('_');
          try {
            const messages = await api.getPrivateMessages(chatId, 1);
            const lastMessage = messages[0];
            return {
              id: chatId,
              type: 'private',
              friendId: friend.friendId,
              name: friend.friendName || friend.friendEmail?.split('@')[0] || friend.friendId,
              avatar: friend.friendAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${friend.friendId}`,
              lastMessage: lastMessage?.content || '',
              lastTime: lastMessage?.createdAt || friend.createdAt,
              unread: 0
            };
          } catch {
            return null;
          }
        })
      );

      const groupChats = groups
        .filter(g => g.type === 'user_group')
        .map(group => ({
          id: group.id,
          type: 'group',
          name: group.name,
          avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${group.id}`,
          lastMessage: group.lastMessage || '',
          lastTime: group.updatedAt || group.createdAt,
          memberCount: group.memberCount || 2
        }));

      const allChats = [...privateChats.filter(Boolean), ...groupChats]
        .sort((a, b) => new Date(b.lastTime) - new Date(a.lastTime));

      setRecentChats(allChats);
    } catch (err) {
      console.error('加载聊天列表失败:', err);
    }
  };

  const handleTouchStart = (e, chatId) => {
    startXRef.current = e.touches[0].clientX;
    setSwipeId(chatId);
  };

  const handleTouchMove = (e, chatId) => {
    if (startXRef.current === null) return;
    const diff = e.touches[0].clientX - startXRef.current;
    if (diff < 0) {
      setSwipeX(Math.max(diff, -80));
    }
  };

  const handleTouchEnd = (chatId) => {
    if (swipeX < -40) {
      setSwipeX(-80);
    } else {
      setSwipeX(0);
    }
    startXRef.current = null;
  };

  const handleDelete = async (chat) => {
    if (chat.type === 'private') {
      if (window.confirm('确定要删除该聊天记录吗？')) {
        setRecentChats(prev => prev.filter(c => c.id !== chat.id));
      }
    } else {
      if (window.confirm('确定要退出该群聊吗？')) {
        setRecentChats(prev => prev.filter(c => c.id !== chat.id));
      }
    }
    setSwipeX(0);
    setSwipeId(null);
  };

  const handleClick = (chat) => {
    if (swipeX !== 0) {
      setSwipeX(0);
      return;
    }
    
    if (chat.type === 'private') {
      const friend = {
        friendId: chat.friendId,
        friendName: chat.name,
        friendAvatar: chat.avatar
      };
      onOpenPrivateChat?.(friend);
    } else {
      onOpenGroupChat?.(chat.id, chat.name);
    }
    onClose();
  };

  const formatTime = (time) => {
    if (!time) return '';
    const date = new Date(time);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return '刚刚';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`;
    return date.toLocaleDateString('zh-CN');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[#050510]">
      <div className="h-28 flex items-end px-6 pb-4 glass-card rounded-b-[40px]">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10">
            <ChevronLeft size={24} className="text-white" />
          </button>
          <h1 className="text-xl font-bold text-white">Chat</h1>
        </div>
      </div>

      <div className="p-4 overflow-y-auto" style={{ height: 'calc(100vh - 112px)' }}>
        {recentChats.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400">暂无聊天记录</p>
            <p className="text-gray-500 text-sm mt-2">去添加好友开始聊天吧~</p>
          </div>
        ) : (
          <div className="space-y-2">
            {recentChats.map((chat) => (
              <div
                key={chat.id}
                className="relative overflow-hidden rounded-2xl"
                style={{ height: '72px' }}
              >
                <div
                  className="absolute right-0 top-0 bottom-0 w-20 bg-red-500 flex items-center justify-center"
                  onClick={() => handleDelete(chat)}
                >
                  <Trash2 size={20} className="text-white" />
                </div>
                
                <div
                  className="absolute inset-0 bg-[#1a1a2e] flex items-center px-4 transition-transform"
                  style={{
                    transform: swipeId === chat.id ? `translateX(${swipeX}px)` : 'translateX(0)'
                  }}
                  onTouchStart={(e) => handleTouchStart(e, chat.id)}
                  onTouchMove={(e) => handleTouchMove(e, chat.id)}
                  onTouchEnd={() => handleTouchEnd(chat.id)}
                  onClick={() => handleClick(chat)}
                >
                  <img
                    src={chat.avatar}
                    alt={chat.name}
                    className="w-12 h-12 rounded-full bg-black"
                  />
                  <div className="flex-1 ml-3 min-w-0">
                    <div className="flex items-center gap-2">
                      {chat.type === 'group' && <Users size={14} className="text-gray-400" />}
                      <span className="text-white font-medium truncate">{chat.name}</span>
                      {chat.type === 'group' && (
                        <span className="text-gray-500 text-xs">({chat.memberCount})</span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm truncate">{chat.lastMessage || '暂无消息'}</p>
                  </div>
                  <span className="text-gray-500 text-xs ml-2">{formatTime(chat.lastTime)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatList;
